import type {
  Blackout,
  CalendarLeave,
  CalendarPayload,
  LeaveTypeId,
} from '@lms/shared-types'
import {
  COVERAGE_THRESHOLDS,
} from './calendar-thresholds'
import { HIDDEN_LEAVE_TYPE } from './leave-type-palette'

/**
 * Filter shape consumed by the calendar derive functions. Structurally
 * compatible with `CalendarFilters` exported from CalendarToolbar — kept
 * here in `lib/` to avoid a circular dependency between `lib/` and
 * `components/`.
 */
export interface CalendarFilters {
  teams: ReadonlySet<string>
  leaveTypes: ReadonlySet<LeaveTypeId>
  showBlackouts: boolean
  showHolidays: boolean
  showPending: boolean
}

/**
 * The shape consumed by MonthGrid's dayMeta map. Defined here as a local
 * structural copy so calendar-derive doesn't import a component module
 * (which would invert the dependency direction). MonthGrid's
 * MonthGridDayMeta interface mirrors this shape.
 */
export interface DayMetaWithSignals {
  publicHoliday?: string
  isToday?: boolean
  coverageWarning?: 'medium' | 'high'
}

export interface CoverageResult {
  level: 'medium' | 'high' | undefined
  teamId: string | undefined
  ratio: number
}

function isWithin(day: string, start: string, end: string): boolean {
  return day >= start && day <= end
}

function shiftDate(date: string, deltaDays: number): string {
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return date
  const next = new Date(Date.UTC(y, m - 1, d + deltaDays))
  const yy = next.getUTCFullYear()
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(next.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/**
 * For one ISO date, return the worst-hit team's coverage level. If
 * `visibleTeams` is empty it means "all teams" — every team is considered.
 * Counts unique employees per team (deduplicating overlapping records).
 */
export function coverageForDay(
  date: string,
  leaves: ReadonlyArray<CalendarLeave>,
  teamHeadcount: ReadonlyMap<string, number>,
  visibleTeams: ReadonlySet<string>,
): CoverageResult {
  const teamEmployees = new Map<string, Set<string>>()
  for (const leave of leaves) {
    if (!isWithin(date, leave.startDate, leave.endDate)) continue
    if (visibleTeams.size > 0 && !visibleTeams.has(leave.teamId)) continue
    const set = teamEmployees.get(leave.teamId) ?? new Set<string>()
    set.add(leave.employeeId)
    teamEmployees.set(leave.teamId, set)
  }
  let worstTeam: string | undefined
  let worstRatio = 0
  for (const [teamId, employees] of teamEmployees) {
    const headcount = teamHeadcount.get(teamId) ?? 0
    if (headcount === 0) continue
    const ratio = employees.size / headcount
    if (ratio > worstRatio) {
      worstRatio = ratio
      worstTeam = teamId
    }
  }
  let level: 'medium' | 'high' | undefined
  if (worstRatio >= COVERAGE_THRESHOLDS.high) level = 'high'
  else if (worstRatio >= COVERAGE_THRESHOLDS.medium) level = 'medium'
  return { level, teamId: worstTeam, ratio: worstRatio }
}

/**
 * True when any day in `[leave.startDate, leave.endDate]` is inside any
 * blackout whose teamIds list contains the leave's team.
 */
export function leaveConflictsBlackout(
  leave: CalendarLeave,
  blackouts: ReadonlyArray<Blackout>,
): boolean {
  for (const bo of blackouts) {
    if (!bo.teamIds.includes(leave.teamId)) continue
    if (leave.endDate < bo.startDate || leave.startDate > bo.endDate) continue
    return true
  }
  return false
}

/**
 * Compose all signals into a per-date map consumable by MonthGrid's
 * dayMeta prop. Pure function — same input always produces same output.
 */
export function buildDayMetaWithSignals(
  payload: CalendarPayload,
  filters: CalendarFilters,
  teamHeadcount: ReadonlyMap<string, number>,
): ReadonlyMap<string, DayMetaWithSignals> {
  const meta = new Map<string, DayMetaWithSignals>()

  const visibleLeaves = payload.leaves.filter((l) => {
    if (filters.teams.size > 0 && !filters.teams.has(l.teamId)) return false
    // Type-hidden leaves (privacy sentinel) always pass the type filter.
    if (l.type !== HIDDEN_LEAVE_TYPE && !filters.leaveTypes.has(l.type))
      return false
    if (l.status === 'pending' && !filters.showPending) return false
    return true
  })

  if (filters.showHolidays) {
    for (const ph of payload.publicHolidays) {
      const existing = meta.get(ph.date) ?? {}
      meta.set(ph.date, { ...existing, publicHoliday: ph.name })
    }
  }

  const datesOfInterest = new Set<string>()
  for (const l of visibleLeaves) {
    let cur = l.startDate
    while (cur <= l.endDate) {
      datesOfInterest.add(cur)
      cur = shiftDate(cur, 1)
    }
  }
  if (filters.showHolidays) {
    for (const ph of payload.publicHolidays) datesOfInterest.add(ph.date)
  }

  for (const date of datesOfInterest) {
    const cov = coverageForDay(date, visibleLeaves, teamHeadcount, filters.teams)
    if (cov.level !== undefined) {
      const existing = meta.get(date) ?? {}
      meta.set(date, {
        ...existing,
        coverageWarning: cov.level,
      })
    }
  }

  return meta
}

